import { AppUser, IRoom, spLogTags } from '@eventr/common'
import { ColoursObj, Icon, Typography } from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { MemberProfilePic } from '../../../home/team/MemberProfilePic'
import { useBoundStore } from '../../../state/bound.store'
import { useLanguageContext } from '../../../state/languageState/languageContext'
import { RocketChannel } from '../../../state/rocketchat/rocketchat.interface'
import { liveToken } from '../../../state/rocketchatState/rocketchatSignals'
import { Utils } from '../../../utils'
import { useGetChatName } from '../hooks/useGetChatName'
import {
    getChatTypeFromSectionId,
    getSectionIdFromChatType,
    useRocketChatContext,
} from '../rocketChatProvider'
export type ChatTypes =
    | 'User'
    | 'Group'
    | 'Flight'
    | 'Activity'
    | 'Accommodation'
    | 'Transport'
    | 'HireCar'
    | 'ParkingBooking'
    | 'VehicleAllocation'

export type ChatCardType = {
    room: IRoom
    channel: RocketChannel
}

export const ChatCard: React.FC<ChatCardType> = ({ room, channel }) => {
    const [userInfo, setUserInfo] = useState<AppUser | string>()
    const { users, user } = useBoundStore(
        useShallow((s) => {
            return {
                users: s.users,
                user: s.wpUser,
            }
        })
    )
    const { openChat, markAsRead, getRooms, userSubscriptions } =
        useRocketChatContext()
    const chatName = useGetChatName(channel.channel_id ?? '')
    const type = useMemo(() => {
        if (channel.type === 'created_room') {
            return 'Group'
        }
        const type = getChatTypeFromSectionId(channel.section_id)
        return type
    }, [channel])

    function getTimeOrDate() {
        try {
            if (room && room.lm && room.lm['$date']) {
                const date = new Date(Math.floor(room.lm['$date']))

                const day = date.getDate()
                const month = date.getMonth() + 1
                const year = date.getFullYear()

                const currentDateTime = new Date()

                if (
                    day === currentDateTime.getDate() &&
                    month === currentDateTime.getMonth() + 1 &&
                    year === currentDateTime.getFullYear()
                ) {
                    return DateTime.fromJSDate(date).toFormat(
                        useLanguageContext.getState().timeFormat,
                        {
                            locale: useLanguageContext
                                .getState()
                                .getLanguageISO639Code(),
                        }
                    )
                } else if (year !== currentDateTime.getFullYear()) {
                    return DateTime.fromJSDate(date).toFormat('EEE, MMM d ', {
                        locale: useLanguageContext
                            .getState()
                            .getLanguageISO639Code(),
                    })
                } else {
                    return DateTime.fromJSDate(date).toFormat('EEE, MMM d ', {
                        locale: useLanguageContext
                            .getState()
                            .getLanguageISO639Code(),
                    })
                }
            } else if (room && room.ts && room.ts['$date']) {
                const date = new Date(room.ts['$date'])

                const day = date.getDate()
                const month = date.getMonth() + 1
                const year = date.getFullYear()

                const currentDateTime = new Date()

                if (
                    day === currentDateTime.getDate() &&
                    month === currentDateTime.getMonth() + 1 &&
                    year === currentDateTime.getFullYear()
                ) {
                    return DateTime.fromJSDate(date).toFormat(
                        useLanguageContext.getState().timeFormat,
                        {
                            locale: useLanguageContext
                                .getState()
                                .getLanguageISO639Code(),
                        }
                    )
                } else if (year !== currentDateTime.getFullYear()) {
                    return DateTime.fromJSDate(date).toFormat('EEE, MMM d ', {
                        locale: useLanguageContext
                            .getState()
                            .getLanguageISO639Code(),
                    })
                } else {
                    return DateTime.fromJSDate(date).toFormat('EEE, MMM d ', {
                        locale: useLanguageContext
                            .getState()
                            .getLanguageISO639Code(),
                    })
                }
            }
        } catch {
            spLogTags('Error fetching time or date of message', [
                'rocketchat',
                'ERROR',
            ])
            return ''
        }
    }

    const getUser = useCallback(
        function (channel: RocketChannel) {
            if (channel && channel.type === 'direct') {
                const otherUserId = channel.members?.find((u) => u !== user?.id)
                setUserInfo(users?.find((u) => u.id === otherUserId))
            } else {
                setUserInfo(undefined)
            }
        },
        [users, user]
    )

    const isUnread = useMemo(() => {
        const sub = userSubscriptions?.find((s) => s.rid === room._id)
        if (sub) {
            return sub.alert
        }

        return false
    }, [userSubscriptions, room])

    useEffect(() => {
        channel && getUser(channel)
    }, [channel, getUser])

    return (
        <Box
            height={'40px'}
            padding={'8px 16px'}
            display={'flex'}
            flexDirection={'row'}
            gap={'8px'}
            style={{
                backgroundColor: isUnread
                    ? ColoursObj.core.secondary + '14'
                    : ColoursObj.backgrounds.primary,
            }}
            onClick={async () => {
                Utils.hapticFeedback('light')
                const groupType = getSectionIdFromChatType(type)
                openChat(room._id, room.t + groupType)
                if (liveToken.value) {
                    const resp = await markAsRead(room._id)
                    if (resp === true) {
                        getRooms()
                    }
                }
            }}
        >
            <Stack height={'40px'} width={'40px'}>
                <div
                    style={{
                        height: '40px',
                        width: '40px',
                        borderRadius: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: ColoursObj.shades.shade2,
                    }}
                >
                    {type === 'User' &&
                    typeof userInfo !== 'string' &&
                    userInfo ? (
                        <MemberProfilePic user={userInfo} sideLength={40} />
                    ) : (
                        <Icon
                            iconColour={ColoursObj.shades.shade5}
                            name={
                                type === 'User'
                                    ? 'User'
                                    : type === 'Accommodation'
                                    ? 'Stays'
                                    : type === 'Flight'
                                    ? 'Flight'
                                    : type === 'Activity'
                                    ? 'Activity'
                                    : type === 'Transport'
                                    ? 'Ferry'
                                    : type === 'ParkingBooking'
                                    ? 'Parking'
                                    : type === 'HireCar'
                                    ? 'Hire_Car'
                                    : type === 'VehicleAllocation'
                                    ? 'Car_Allocation'
                                    : type === 'Group'
                                    ? 'Users'
                                    : 'Users'
                            }
                        />
                    )}
                </div>
            </Stack>
            <Stack
                height={'40px'}
                width={'calc(100% - 48px)'}
                justifyContent={'center'}
            >
                <Stack
                    height={'24px'}
                    justifyContent={'space-between'}
                    display={'flex'}
                    flexDirection={'row'}
                >
                    <Stack
                        display={'flex'}
                        gap={'8px'}
                        flexDirection={'row'}
                        alignItems={'center'}
                    >
                        <Typography
                            variant={isUnread ? 'bodyBold' : 'bodyRegular'}
                            maxWidth={'calc(100vw - 150px)'}
                            overflow={'hidden'}
                            textOverflow={'ellipsis'}
                            whiteSpace={'nowrap'}
                            height={'24px'}
                            color={ColoursObj.core.primary}
                        >
                            {chatName}
                        </Typography>
                        {isUnread && (
                            <div
                                style={{
                                    height: '8px',
                                    width: '8px',
                                    borderRadius: '4px',
                                    backgroundColor:
                                        ColoursObj.backgrounds.secondary,
                                }}
                            />
                        )}
                    </Stack>
                    <Stack justifyContent={'center'} display={'flex'}>
                        <Typography
                            variant="bodySubtext"
                            color={ColoursObj.shades.shade1}
                            fontSize={'12px'}
                        >
                            {getTimeOrDate()}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack height={room.lastMessage ? '16px' : '0px'}>
                    <Typography
                        variant="bodySubtext"
                        fontSize={'12px'}
                        fontWeight={isUnread ? '500' : '300'}
                        overflow={'hidden'}
                        textOverflow={'ellipsis'}
                        whiteSpace={'nowrap'}
                        color={ColoursObj.core.primary}
                    >
                        {room.lastMessage && room.lastMessage.msg}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    )
}
